import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { endOfDay, format, setHours, setMinutes } from 'date-fns';
import CustomDatePicker from './CustomDatePicker';
import Icon from './Icon';
import '../css/sort-menu.css';

const OrdersSortFiltersMenu = ({
  sort,
  direction,
  onSortChange,
  onDirectionChange,
  createdFrom,
  createdTo,
  neverBought,
  id = '',
  onDateFromChange,
  onDateToChange,
  onNeverBoughtToggle,
  statusDates = {},
  onStatusDateChange,
  onIdChange,
  translationKey = 'orders'
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const sortOptions = [
    { value: 'updatedAt', label: t(`${translationKey}.filters.sortByDate`) },
    { value: 'createdAt', label: t(`${translationKey}.filters.sortByCreatedDate`) },
    { value: 'id', label: t(`${translationKey}.filters.id`) }
  ];

  const directionOptions = [
    { value: 'DESC', label: t(`${translationKey}.filters.descending`), icon: '↓' },
    { value: 'ASC', label: t(`${translationKey}.filters.ascending`), icon: '↑' }
  ];

  const handleSortSelect = (value) => {
    onSortChange(value);
  };

  const handleDirectionSelect = (value) => {
    onDirectionChange(value);
  };

  const normalizeDateStartOfDay = (date) => {
    if (!date) return '';
    return format(setMinutes(setHours(date, 0), 0), "yyyy-MM-dd'T'HH:mm");
  };

  // «До» включает весь выбранный день
  const normalizeDateEndOfDay = (date) => {
    if (!date) return '';
    return format(endOfDay(date), "yyyy-MM-dd'T'HH:mm:ss.SSS");
  };

  const statusDateRows = [
    { status: 'IN_WORK', fromKey: 'inWorkFrom', toKey: 'inWorkTo' },
    { status: 'DONE', fromKey: 'doneFrom', toKey: 'doneTo' },
    { status: 'CANCELED', fromKey: 'canceledFrom', toKey: 'canceledTo' }
  ];

  const hasStatusDates = Object.values(statusDates).some(Boolean);

  const isSortMenuActive = useMemo(() => {
    const sortNonDefault = sort !== 'updatedAt' || direction !== 'DESC';
    const extra =
      Boolean(createdFrom) ||
      Boolean(createdTo) ||
      Boolean(id && String(id).trim()) ||
      neverBought === true ||
      hasStatusDates;
    return sortNonDefault || extra;
  }, [sort, direction, createdFrom, createdTo, id, neverBought, hasStatusDates]);

  return (
    <div className="sort-menu-wrapper orders-sort-filters-wrapper" ref={menuRef}>
      <button
        type="button"
        className={`sort-menu-button${isSortMenuActive ? ' filter-active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t(`${translationKey}.filters.sortButton`)}
      >
        <span className="sort-menu-icon">
          <Icon name="sort-arrows" size={14} />
        </span>
        <span className="sort-menu-label">{t(`${translationKey}.filters.sortButton`)}</span>
        <span className="sort-menu-arrow">
          <Icon
            name="chevron-down"
            size={12}
            className={`expand-icon ${isOpen ? 'open' : ''}`}
          />
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="sort-menu-dropdown orders-sort-filters-dropdown"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <div className="orders-sort-main-grid">
              <div className="sort-menu-section">
                <div className="sort-menu-section-title">{t(`${translationKey}.filters.sortBy`)}</div>
                <div className="sort-menu-options">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`sort-menu-option ${sort === option.value ? 'active' : ''}`}
                      onClick={() => handleSortSelect(option.value)}
                    >
                      <span className="sort-menu-option-label">{option.label}</span>
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className={`sort-menu-option orders-never-bought-row ${neverBought === true ? 'active' : ''}`}
                  role="switch"
                  aria-checked={neverBought === true}
                  aria-label={t(`${translationKey}.filters.neverBought`)}
                  onClick={() => onNeverBoughtToggle(!(neverBought === true))}
                >
                  <span className="orders-never-bought-label">
                    {t(`${translationKey}.filters.neverBought`)}
                  </span>
                  <span className={`orders-switch ${neverBought === true ? 'active' : ''}`} aria-hidden="true">
                    <span className="orders-switch-track">
                      <span className="orders-switch-thumb" />
                    </span>
                  </span>
                </button>
              </div>

              <div className="sort-menu-section">
                <div className="sort-menu-section-title">
                  {sort === 'id' ? t(`${translationKey}.filters.id`) : t(`${translationKey}.filters.sortDirection`)}
                </div>
                <div className="sort-menu-options">
                  {sort === 'id' ? (
                    <div className="sort-menu-input-wrapper">
                      <input
                        type="text"
                        className="sort-menu-text-input"
                        placeholder={t(`${translationKey}.filters.idPlaceholder`)}
                        value={id || ''}
                        onChange={(e) => onIdChange(e.target.value)}
                        style={{
                          border: '2px solid var(--accent-primary)',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          width: '100%',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  ) : (
                    directionOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`sort-menu-option ${direction === option.value ? 'active' : ''}`}
                        onClick={() => handleDirectionSelect(option.value)}
                      >
                        <span className="sort-menu-option-icon">{option.icon}</span>
                        <span className="sort-menu-option-label">{option.label}</span>
                      </button>
                    ))
                  )}
                </div>
                {sort === 'createdAt' && (
                  <div className="orders-sort-date-range">
                    <div className="orders-sort-date-range-grid">
                      <div className="more-filters-item">
                        <label className="sort-menu-section-title orders-date-inline-title">
                          {t(`${translationKey}.filters.dateFrom`)}
                        </label>
                        <CustomDatePicker
                          selected={createdFrom ? new Date(createdFrom) : null}
                          showTimeSelect={false}
                          onChange={(date) => onDateFromChange(normalizeDateStartOfDay(date))}
                        />
                      </div>

                      <div className="more-filters-item">
                        <label className="sort-menu-section-title orders-date-inline-title">
                          {t(`${translationKey}.filters.dateTo`)}
                        </label>
                        <CustomDatePicker
                          selected={createdTo ? new Date(createdTo) : null}
                          showTimeSelect={false}
                          onChange={(date) => onDateToChange(normalizeDateStartOfDay(date))}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {onStatusDateChange && (
              <div className="sort-menu-section orders-status-dates">
                <div className="sort-menu-section-title">
                  {t(`${translationKey}.filters.statusDatesTitle`)}
                </div>
                {statusDateRows.map(({ status, fromKey, toKey }) => (
                  <div key={status} className="orders-status-dates-row">
                    <div className="orders-status-dates-label">
                      {t(`${translationKey}.filters.statusDates.${status}`)}
                    </div>
                    <div className="more-filters-item">
                      <label className="sort-menu-section-title orders-date-inline-title">
                        {t(`${translationKey}.filters.dateFrom`)}
                      </label>
                      <CustomDatePicker
                        selected={statusDates[fromKey] ? new Date(statusDates[fromKey]) : null}
                        showTimeSelect={false}
                        onChange={(date) => onStatusDateChange(fromKey, normalizeDateStartOfDay(date))}
                      />
                    </div>
                    <div className="more-filters-item">
                      <label className="sort-menu-section-title orders-date-inline-title">
                        {t(`${translationKey}.filters.dateTo`)}
                      </label>
                      <CustomDatePicker
                        selected={statusDates[toKey] ? new Date(statusDates[toKey]) : null}
                        showTimeSelect={false}
                        onChange={(date) => onStatusDateChange(toKey, normalizeDateEndOfDay(date))}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrdersSortFiltersMenu;

